'use client';

import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/useToast';
import { client, queryClient } from '@/lib/api';
import { authClient } from '@/lib/auth';
import { logger } from '@/lib/logger';

export default function ImpersonationBanner() {
  // Use shared session store — avoids a separate HTTP call
  const { data: session, isPending: isLoading } = authClient.useSession();

  // Check if currently impersonating (session.session.impersonatedBy is the admin's ID)
  const impersonatedBy = (session as any)?.session?.impersonatedBy;
  const currentUser = session?.user;

  // Stop impersonation mutation
  const stopMutation = useMutation({
    mutationFn: async () => {
      // Call better-auth's stop endpoint directly
      const result = await authClient.admin.stopImpersonating();

      logger.info('User impersonation stopped', {
        adminUserId: impersonatedBy,
        impersonatedUserId: currentUser?.id,
      });

      return result;
    },
    onSuccess: () => {
      toast.success('Stopped impersonation');
      // Refresh session and redirect
      queryClient.invalidateQueries({ queryKey: ['session'] });
      window.location.href = '/admin/users';
    },
    onError: (error) => {
      logger.error('Stop impersonation failed', { error });
      logger.captureException(error as Error, {
        tags: { feature: 'admin-impersonation', action: 'stop' },
      });
      toast.error(`Failed to stop impersonation: ${(error as Error).message}`);
    },
  });

  // Don't render if not impersonating
  if (isLoading || !impersonatedBy) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-3 sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm">
              Impersonating User: {currentUser?.name} ({currentUser?.email})
            </p>
            <p className="text-xs opacity-90">
              You are viewing the application as this user. Actions will be
              performed on their behalf.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => stopMutation.mutate()}
          disabled={stopMutation.isPending}
          className="bg-white text-red-600 hover:bg-red-50 border-white flex-shrink-0"
        >
          <X className="h-4 w-4 mr-2" />
          Stop Impersonating
        </Button>
      </div>
    </div>
  );
}
